import React from 'react';
import { motion } from 'framer-motion';
import {
  RiDatabaseLine,
  RiFilter3Line,
  RiLayoutMasonryLine,
  RiRadarLine,
  RiBarChartGroupedLine,
  RiLightbulbFlashLine,
  RiFocus3Line
} from 'react-icons/ri';
import './ReconcileSection.css';

const DecorCollect = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <circle cx="30" cy="40" r="4" fill="#cbd5e1" />
    <circle cx="170" cy="60" r="3" fill="#94a3b8" />
    <circle cx="40" cy="160" r="5" fill="#e2e8f0" />
    <circle cx="160" cy="150" r="3" fill="#cbd5e1" />
    <rect x="90" y="20" width="6" height="6" fill="#94a3b8" />
  </svg>
);

const DecorClean = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <line x1="20" y1="100" x2="50" y2="100" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" />
    <line x1="150" y1="100" x2="180" y2="100" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" />
    <circle cx="40" cy="100" r="3" fill="#94a3b8" />
    <circle cx="160" cy="100" r="3" fill="#94a3b8" />
  </svg>
);

const DecorStructure = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <rect x="25" y="40" width="8" height="8" fill="#cbd5e1" />
    <rect x="25" y="55" width="8" height="8" fill="#94a3b8" />
    <rect x="165" y="140" width="8" height="8" fill="#cbd5e1" />
    <rect x="165" y="155" width="8" height="8" fill="#94a3b8" />
  </svg>
);

const DecorMine = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <path d="M 20 40 L 40 80 M 160 120 L 180 160" stroke="#e2e8f0" strokeWidth="2" />
    <circle cx="20" cy="40" r="3" fill="#cbd5e1" />
    <circle cx="180" cy="160" r="3" fill="#cbd5e1" />
  </svg>
);

const DecorAnalyse = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <rect x="30" y="140" width="6" height="20" fill="#cbd5e1" />
    <rect x="40" y="130" width="6" height="30" fill="#94a3b8" />
    <rect x="150" y="40" width="6" height="30" fill="#cbd5e1" />
    <rect x="160" y="20" width="6" height="50" fill="#94a3b8" />
  </svg>
);

const DecorInsight = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <circle cx="100" cy="100" r="85" fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="4 6" />
    <circle cx="100" cy="100" r="95" fill="none" stroke="#f1f5f9" strokeWidth="2" />
  </svg>
);

const DecorDecision = () => (
  <svg className="step-decor" viewBox="0 0 200 200">
    <circle cx="100" cy="100" r="85" fill="none" stroke="#cbd5e1" strokeWidth="1.5" />
    <path d="M 100 5 L 100 15 M 100 185 L 100 195 M 5 100 L 15 100 M 185 100 L 195 100" stroke="#94a3b8" strokeWidth="2" />
  </svg>
);

const stepsData = [
  { num: '01', title: 'Collect', desc: <>Gather scattered<br />data sources</>, icon: <RiDatabaseLine />, Decor: DecorCollect },
  { num: '02', title: 'Clean', desc: 'Remove errors and duplicates', icon: <RiFilter3Line />, Decor: DecorClean },
  { num: '03', title: 'Structure', desc: 'Organize into usable formats', icon: <RiLayoutMasonryLine />, Decor: DecorStructure },
  { num: '04', title: 'Mine', desc: <>Discover hidden<br />patterns</>, icon: <RiRadarLine />, Decor: DecorMine },
  { num: '05', title: 'Analyse', desc: <>Interpret complex<br />metrics</>, icon: <RiBarChartGroupedLine />, Decor: DecorAnalyse },
  { num: '06', title: 'Insight', desc: 'Generate actionable knowledge', icon: <RiLightbulbFlashLine />, Decor: DecorInsight },
  { num: '07', title: 'Decision', desc: 'Drive strategic business outcomes', icon: <RiFocus3Line />, Decor: DecorDecision },
];

const ReconcileSection = () => {
  return (
    <section className="process-section">
      <div className="container">
        
        <motion.div 
          className="process-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
        >
          <span className="process-eyebrow">OUR APPROACH</span>
          <h2 className="process-title">Turn Raw Data Into Useful Intelligence.</h2>
          <p className="process-desc">
            We collect, clean, structure, mine and analyse data to identify trends, patterns, anomalies and business opportunities.
          </p>
        </motion.div>

        <div className="process-timeline-wrapper">
          <motion.div 
            className="process-timeline"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={{
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
              hidden: { opacity: 0 }
            }}
          >
            {stepsData.map((step, idx) => (
              <motion.div 
                key={step.num} 
                className="process-step"
                variants={{
                  visible: { opacity: 1, x: 0 },
                  hidden: { opacity: 0, x: -20 }
                }}
              >
                <div className="process-ring-container">
                  <step.Decor />
                  <div className="process-ring">
                    <span className="process-num">{step.num}</span>
                    <div className="process-icon">{step.icon}</div>
                    <span className="process-label">{step.title}</span>
                  </div>
                </div>
                
                <div className="process-text">
                  {step.desc}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default ReconcileSection;